import ConceptSwitcher from "@/components/ConceptSwitcher";
import BrandMark, { TAGLINE } from "@/components/BrandMark";
import { PHOTOS } from "@/constants/photos";
import {
  ArrowUpRight,
  ArrowRight,
  Compass,
  ChalkboardTeacher,
  Users,
  ShieldCheck,
  Microphone,
  ArrowsLeftRight,
  Path,
} from "@phosphor-icons/react";

// Concept 02 — Systems That Hold palette
const C = {
  bg: "#F5F6F3", // Mineral White
  surface: "#E7ECE7", // Pale Sage
  text: "#202628", // Graphite
  brand: "#245E63", // Structural Teal
  rose: "#B98586", // Clay Rose
  muted: "#697175", // Steel Gray
  line: "#D7DDDA", // Soft Grid
  inverse: "#294347", // Forest Slate
  linen: "#F8F7F2", // Linen
};

const navLinks = ["Who We Help", "Pathways", "Process", "Speaking", "Insights", "Contact"];

const problemPathways = [
  {
    problem: "Leadership transition or succession risk",
    pathway: "Advisory + Coaching",
    pathwayCode: "S01 · S03",
  },
  {
    problem: "Supervisory inconsistency or manager exposure",
    pathway: "Supervisory Readiness",
    pathwayCode: "S02",
  },
  {
    problem: "Conduct, culture, or trust breakdown",
    pathway: "Sensitive Facilitation + Advisory",
    pathwayCode: "S04 · S01",
  },
  {
    problem: "Executive isolation or stretched senior team",
    pathway: "Executive Coaching",
    pathwayCode: "S03",
  },
  {
    problem: "Mission–operations drift or values misalignment",
    pathway: "Advisory + Speaking",
    pathwayCode: "S01 · S05",
  },
  {
    problem: "Growth outpacing people infrastructure",
    pathway: "Advisory + Supervisory Readiness",
    pathwayCode: "S01 · S02",
  },
];

const pathways = [
  {
    code: "S01",
    Icon: Compass,
    label: "Advisory for leadership and people strategy",
    short: "Strategic counsel for the people decisions that shape the next phase.",
  },
  {
    code: "S02",
    Icon: ChalkboardTeacher,
    label: "Supervisory readiness and manager development",
    short: "Practical training that closes the gap between policy and supervisor.",
  },
  {
    code: "S03",
    Icon: Users,
    label: "Executive and leadership coaching",
    short: "Coaching scoped to real organizational stakes — not abstract self-work.",
  },
  {
    code: "S04",
    Icon: ShieldCheck,
    label: "Facilitation for sensitive people and culture moments",
    short: "Legal-informed facilitation when conduct, culture, or trust needs care.",
  },
  {
    code: "S05",
    Icon: Microphone,
    label: "Speaking and learning experiences",
    short: "Authority content delivered into your team or audience.",
  },
];

const processArch = [
  {
    n: "01",
    label: "Discover",
    body: "Surface the real moment. Understand context, stakeholders, stakes, and the boundary conditions of the work.",
  },
  {
    n: "02",
    label: "Assess",
    body: "Diagnose what is happening beneath the symptoms — across leadership, supervisory, culture, and conduct dimensions.",
  },
  {
    n: "03",
    label: "Align",
    body: "Bring leaders into shared language. Make the decision space legible to the people who will carry the work forward.",
  },
  {
    n: "04",
    label: "Support",
    body: "Embed practical capability. Advise, train, coach, or facilitate until the new pattern is durable.",
  },
];

const insights = [
  {
    label: "Framework · Supervisory readiness",
    title: "A four-part model for closing the manager gap in mission-driven orgs.",
    read: "8 min read",
  },
  {
    label: "Field guide · Transitions",
    title: "Building succession architecture before the seat opens.",
    read: "6 min read",
  },
  {
    label: "Essay · Culture systems",
    title: "Culture is downstream of how supervisors handle small moments.",
    read: "5 min read",
  },
];

export default function ConceptB() {
  return (
    <main
      data-testid="concept-b-page"
      className="min-h-screen font-manrope antialiased"
      style={{ background: C.bg, color: C.text }}
    >
      <ConceptSwitcher tone="b" />

      {/* Navigation */}
      <header className="border-b" style={{ background: C.bg, borderColor: C.line }}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 h-28 flex items-center justify-between">
          <div className="flex items-end gap-5">
            <BrandMark href="/concept-b" size="lg" testId="nav-wordmark" />
            <span
              className="hidden xl:inline-flex items-center font-sora text-[12px] leading-tight pb-2 border-l pl-5 max-w-[240px]"
              style={{ color: C.brand, borderColor: `${C.brand}55` }}
            >
              {TAGLINE}
            </span>
          </div>

          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navLinks.map((l) => (
              <a
                key={l}
                href="#"
                data-testid={`nav-link-${l.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-sora text-[12px] uppercase tracking-[0.16em] font-medium transition-colors whitespace-nowrap"
                style={{ color: `${C.text}C0` }}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.brand)}
                onMouseLeave={(e) => (e.currentTarget.style.color = `${C.text}C0`)}
              >
                {l}
              </a>
            ))}
          </nav>

          <a
            href="#cta"
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center gap-2 px-6 py-3 font-sora text-[12px] uppercase tracking-[0.14em] font-medium transition-colors whitespace-nowrap"
            style={{ background: C.brand, color: C.linen }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.inverse)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.brand)}
          >
            Request a consultation
            <ArrowUpRight size={13} weight="bold" />
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b" style={{ borderColor: C.line }}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div
            className="grid lg:grid-cols-12 gap-px border"
            style={{ background: C.line, borderColor: C.line }}
          >
            {/* Left big card */}
            <div
              className="lg:col-span-8 p-8 lg:p-14 fade-up"
              style={{ background: C.linen }}
            >
              <div className="flex items-center gap-3 mb-10">
                <span
                  className="font-sora text-[10px] uppercase tracking-[0.22em] font-semibold"
                  style={{ color: C.brand }}
                >
                  Concept 02 <span style={{ color: C.rose }}>·</span> Systems That Hold
                </span>
                <span className="h-px flex-1" style={{ background: C.line }} />
                <span
                  className="font-sora text-[10px] uppercase tracking-[0.22em]"
                  style={{ color: `${C.text}77` }}
                >
                  Strategic system for organizations
                </span>
              </div>

              <h1
                data-testid="hero-title"
                className="font-sora font-bold text-4xl sm:text-5xl lg:text-[76px] leading-[1.02] tracking-[-0.02em]"
                style={{ color: C.text }}
              >
                Faith 1st helps organizations strengthen{" "}
                <span style={{ color: C.brand }}>leadership, supervision, and culture</span>{" "}
                under pressure.
              </h1>

              <p
                data-testid="hero-subcopy"
                className="mt-10 max-w-2xl text-[17px] lg:text-[19px] leading-[1.6]"
                style={{ color: `${C.text}D5` }}
              >
                Faith&nbsp;1st supports mission-driven organizations navigating
                leadership strain, supervisory gaps, culture challenges, and
                people risks that require thoughtful structure and trusted
                outside support — from advisory to supervisory readiness to
                coaching and facilitation.
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href="#cta"
                  data-testid="hero-primary-cta"
                  className="inline-flex items-center gap-2 px-7 py-3.5 font-sora text-[13px] uppercase tracking-[0.12em] transition-colors"
                  style={{ background: C.brand, color: C.linen }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.inverse)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.brand)}
                >
                  Request a consultation <ArrowRight size={14} weight="bold" />
                </a>
                <a
                  href="#problem-pathway"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-2 border px-7 py-3.5 font-sora text-[13px] uppercase tracking-[0.12em] transition-colors"
                  style={{ borderColor: `${C.text}33`, color: C.text }}
                >
                  See how the work is structured
                </a>
              </div>
            </div>

            {/* Right column — advisory credibility panel */}
            <aside
              className="lg:col-span-4 grid grid-cols-1 gap-px"
              style={{ background: C.line }}
            >
              {/* Founder presence introduced early */}
              <div className="p-7 lg:p-8 flex flex-col" style={{ background: C.linen }}>
                <div className="flex items-center gap-4 mb-5">
                  <div
                    className="relative h-16 w-16 overflow-hidden rounded-full border"
                    style={{ borderColor: C.line }}
                  >
                    <img
                      src={PHOTOS.warmCloseUp}
                      alt="Katrina Bernard"
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="eager"
                    />
                  </div>
                  <div>
                    <p
                      className="font-sora text-[10px] uppercase tracking-[0.22em] font-semibold"
                      style={{ color: C.brand }}
                    >
                      Led by
                    </p>
                    <p
                      className="font-sora text-[15px] font-semibold mt-1"
                      style={{ color: C.text }}
                    >
                      Katrina Bernard
                    </p>
                  </div>
                </div>
                <p
                  className="text-[13.5px] leading-[1.65]"
                  style={{ color: `${C.text}C8` }}
                >
                  An advisory practice for organizations holding sensitive
                  people, leadership, and culture work — supported by judgment
                  built across two decades.
                </p>
              </div>

              {/* Who we work with — reframed less as metric */}
              <div
                className="p-7 lg:p-8 flex flex-col"
                style={{ background: C.surface }}
              >
                <div
                  className="font-sora text-[10px] uppercase tracking-[0.22em] font-semibold mb-4"
                  style={{ color: C.rose }}
                >
                  Who we work with
                </div>
                <p
                  className="font-sora text-[18px] leading-[1.4] font-semibold"
                  style={{ color: C.text }}
                >
                  Mission-driven organizations between{" "}
                  <span style={{ color: C.brand }}>50 and 500 people</span> —
                  carrying weight that policy alone can&rsquo;t answer.
                </p>
              </div>

              {/* Five disciplines panel — preserved but tightened */}
              <div
                className="p-7 lg:p-8 flex flex-col"
                style={{ background: C.linen }}
              >
                <div
                  className="font-sora text-[10px] uppercase tracking-[0.22em] font-semibold mb-4"
                  style={{ color: `${C.text}88` }}
                >
                  Five disciplines · one practice
                </div>
                <ul
                  className="grid grid-cols-2 gap-x-3 gap-y-2 text-[13px]"
                  style={{ color: C.text }}
                >
                  {[
                    "Legal-informed",
                    "HR-aware",
                    "Coaching-led",
                    "Facilitation",
                    "Speaking",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: C.rose }}
                      />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Problem-to-pathway mapping */}
      <section
        id="problem-pathway"
        data-testid="problem-pathway-section"
        className="border-b"
        style={{ background: C.surface, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-7">
              <p
                className="font-sora text-[10px] uppercase tracking-[0.22em] mb-5 font-semibold"
                style={{ color: C.rose }}
              >
                Problem → Pathway · 01
              </p>
              <h2
                className="font-sora font-bold text-3xl sm:text-4xl lg:text-[52px] leading-[1.05] tracking-[-0.02em]"
                style={{ color: C.text }}
              >
                Where organizations get stretched — and{" "}
                <span style={{ color: C.brand }}>how Faith&nbsp;1st helps</span>.
              </h2>
            </div>
            <p
              className="lg:col-span-5 text-[16.5px] leading-[1.65]"
              style={{ color: `${C.text}D0` }}
            >
              People issues rarely stay isolated. They affect supervision,
              trust, communication, performance, and the systems leaders depend
              on. Faith&nbsp;1st helps organizations respond with clear support
              pathways instead of fragmented fixes.
            </p>
          </div>

          <div
            className="border divide-y"
            style={{ background: C.linen, borderColor: C.line, borderTopColor: C.line }}
          >
            {/* Header row */}
            <div
              className="grid grid-cols-12 px-8 py-5"
              style={{ background: C.inverse, color: C.linen }}
            >
              <div
                className="col-span-1 font-sora text-[10.5px] uppercase tracking-[0.24em] font-medium"
                style={{ color: `${C.linen}AA` }}
              >
                No.
              </div>
              <div
                className="col-span-6 font-sora text-[10.5px] uppercase tracking-[0.24em] font-medium"
                style={{ color: `${C.linen}AA` }}
              >
                Problem cluster
              </div>
              <div className="col-span-1 hidden md:block" aria-hidden="true" />
              <div
                className="col-span-4 font-sora text-[10.5px] uppercase tracking-[0.24em] font-medium"
                style={{ color: `${C.linen}AA` }}
              >
                Recommended pathway
              </div>
            </div>

            {problemPathways.map((row, i) => (
              <div
                key={i}
                data-testid={`problem-pathway-${i}`}
                className="grid grid-cols-12 items-center px-8 py-8 transition-colors"
                style={{ borderColor: C.line }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div
                  className="col-span-1 font-sora text-[14px] tracking-[0.1em] font-semibold"
                  style={{ color: C.rose }}
                >
                  0{i + 1}
                </div>
                <div
                  className="col-span-6 font-sora text-[17px] leading-[1.4] pr-6 font-medium"
                  style={{ color: C.text }}
                >
                  {row.problem}
                </div>
                <div className="col-span-1 hidden md:flex items-center justify-center">
                  <ArrowsLeftRight
                    size={18}
                    weight="bold"
                    style={{ color: `${C.brand}55` }}
                  />
                </div>
                <div className="col-span-12 md:col-span-4 mt-3 md:mt-0">
                  <div
                    className="font-sora text-[15px] font-semibold leading-snug"
                    style={{ color: C.brand }}
                  >
                    {row.pathway}
                  </div>
                  <div
                    className="font-sora text-[10.5px] uppercase tracking-[0.22em] mt-1.5 font-medium"
                    style={{ color: `${C.text}88` }}
                  >
                    {row.pathwayCode}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pathways */}
      <section
        id="services"
        data-testid="services-section"
        className="border-b"
        style={{ borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
            <div className="lg:col-span-7">
              <p
                className="font-sora text-[10px] uppercase tracking-[0.22em] mb-5 font-semibold"
                style={{ color: C.rose }}
              >
                Core pathways · 02
              </p>
              <h2
                className="font-sora font-bold text-3xl sm:text-4xl lg:text-[52px] leading-[1.05] tracking-[-0.02em]"
                style={{ color: C.text }}
              >
                Five pathways. <span style={{ color: C.brand }}>One</span> practice.
              </h2>
            </div>
            <p
              className="lg:col-span-5 text-[16.5px] leading-[1.65]"
              style={{ color: `${C.text}D0` }}
            >
              Each pathway can stand alone, but most engagements braid two or
              more together — scoped to the shape of the work you need to do.
            </p>
          </div>

          <div
            className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px border"
            style={{ background: C.line, borderColor: C.line }}
          >
            {pathways.map((p, i) => {
              const Icon = p.Icon;
              return (
                <article
                  key={p.code}
                  data-testid={`pathway-${i}`}
                  className="p-7 lg:p-8 flex flex-col group transition-colors"
                  style={{ background: C.linen }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.linen)}
                >
                  <div className="flex items-center justify-between mb-7">
                    <Icon size={28} weight="duotone" style={{ color: C.brand }} />
                    <span
                      className="font-sora text-[10.5px] uppercase tracking-[0.22em] font-semibold pb-1 border-b"
                      style={{ color: C.brand, borderColor: `${C.brand}55` }}
                    >
                      {p.code}
                    </span>
                  </div>
                  <h3
                    className="font-sora font-bold text-[19px] lg:text-[20px] leading-[1.18] tracking-[-0.01em] mb-4"
                    style={{ color: C.text }}
                  >
                    {p.label}
                  </h3>
                  <p
                    className="text-[14px] leading-[1.7] flex-1"
                    style={{ color: `${C.text}C0` }}
                  >
                    {p.short}
                  </p>
                  <a
                    href="#cta"
                    className="mt-7 inline-flex items-center gap-2 font-sora text-[11px] uppercase tracking-[0.2em] font-semibold pb-1.5 border-b w-fit transition-all"
                    style={{ color: C.brand, borderColor: `${C.brand}55` }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = C.brand;
                      e.currentTarget.style.gap = "12px";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = `${C.brand}55`;
                      e.currentTarget.style.gap = "8px";
                    }}
                  >
                    Pathway detail <ArrowRight size={13} weight="bold" />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process architecture */}
      <section
        data-testid="process-section"
        className="border-b"
        style={{ background: C.inverse, color: C.linen, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-7">
              <p
                className="font-sora text-[10px] uppercase tracking-[0.22em] mb-5 font-semibold"
                style={{ color: C.rose }}
              >
                Process architecture · 03
              </p>
              <h2
                className="font-sora font-bold text-3xl sm:text-4xl lg:text-[52px] leading-[1.05] tracking-[-0.02em]"
                style={{ color: C.linen }}
              >
                A practical structure for{" "}
                <span style={{ color: C.rose }}>complex people work</span>.
              </h2>
            </div>
            <p
              className="lg:col-span-5 text-[16.5px] leading-[1.65]"
              style={{ color: `${C.linen}D0` }}
            >
              Every engagement begins with understanding context, surfacing what
              is most pressing, and identifying support that creates meaningful
              movement. From ambiguity toward alignment, action, and stronger
              practice.
            </p>
          </div>

          <ol
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-px border"
            style={{ background: `${C.linen}26`, borderColor: `${C.linen}26` }}
          >
            {processArch.map((p, i) => (
              <li
                key={p.label}
                data-testid={`process-step-${i}`}
                className="p-9 lg:p-10 flex flex-col relative"
                style={{ background: C.inverse }}
              >
                <div className="flex items-center justify-between mb-8">
                  <span
                    className="font-sora text-[11px] uppercase tracking-[0.22em] font-semibold"
                    style={{ color: C.rose }}
                  >
                    Phase {p.n}
                  </span>
                  {i < processArch.length - 1 && (
                    <Path
                      size={16}
                      weight="bold"
                      className="hidden lg:inline"
                      style={{ color: `${C.linen}55` }}
                    />
                  )}
                </div>
                <h3
                  className="font-sora font-bold text-[32px] lg:text-[34px] leading-[1.05] tracking-[-0.01em] mb-5"
                  style={{ color: C.linen }}
                >
                  {p.label}
                </h3>
                <p
                  className="text-[14.5px] leading-[1.75]"
                  style={{ color: `${C.linen}C8` }}
                >
                  {p.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Founder block — deliberate, full portrait + explicit discipline proof framework */}
      <section
        data-testid="founder-block"
        className="border-b"
        style={{ background: C.linen, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-14 lg:gap-16 items-center">
            {/* Portrait — full, deliberate, not cropped */}
            <div className="lg:col-span-5">
              <figure
                className="relative aspect-[4/5] overflow-hidden border"
                style={{ borderColor: C.line, background: C.surface }}
              >
                <img
                  src={PHOTOS.contemplative}
                  alt="Katrina Bernard — Founder & Principal"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Lower caption block — anchored, not floating */}
                <figcaption
                  className="absolute bottom-0 left-0 right-0 px-6 py-5 backdrop-blur-sm"
                  style={{
                    background: `${C.inverse}E8`,
                    color: C.linen,
                  }}
                >
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div
                        className="font-sora text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-1.5"
                        style={{ color: C.rose }}
                      >
                        Founder · Principal
                      </div>
                      <div
                        className="font-sora font-semibold text-[19px] tracking-[-0.01em]"
                        style={{ color: C.linen }}
                      >
                        Katrina Bernard
                      </div>
                    </div>
                    <div
                      className="font-sora text-[10px] uppercase tracking-[0.22em] text-right"
                      style={{ color: `${C.linen}A8` }}
                    >
                      Faith&nbsp;1st
                    </div>
                  </div>
                </figcaption>
              </figure>
            </div>

            <div className="lg:col-span-7">
              <p
                className="font-sora text-[10.5px] uppercase tracking-[0.24em] mb-6 font-semibold"
                style={{ color: C.brand }}
              >
                Led by experience · 04
              </p>
              <h2
                data-testid="founder-title"
                className="font-sora font-bold text-3xl sm:text-4xl lg:text-[48px] leading-[1.05] tracking-[-0.02em]"
                style={{ color: C.text }}
              >
                Led by experience.{" "}
                <span style={{ color: C.brand }}>Built for organizational reality.</span>
              </h2>
              <p
                className="mt-8 max-w-2xl text-[17px] leading-[1.65]"
                style={{ color: `${C.text}D5` }}
              >
                Faith&nbsp;1st is led by Katrina Bernard, whose practice combines
                strategic judgment with people-centered insight. The work draws
                on five disciplines — held together by one steady point of
                view: organizations rise or fall on how the people inside them
                are led.
              </p>

              {/* Proof framework — explicit, not L/H/C/F shorthand */}
              <div className="mt-12">
                <p
                  className="font-sora text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-5"
                  style={{ color: C.rose }}
                >
                  Five disciplines · how each shows up in the work
                </p>
                <div
                  className="grid sm:grid-cols-2 gap-px border"
                  style={{ background: C.line, borderColor: C.line }}
                >
                  {[
                    {
                      n: "01",
                      label: "Legal-informed",
                      body: "Frames decisions with the employment-law context boards and HR teams actually rely on.",
                    },
                    {
                      n: "02",
                      label: "HR-aware",
                      body: "Operating-level HR judgment built across mission-driven organizations — not theory.",
                    },
                    {
                      n: "03",
                      label: "Coaching-led",
                      body: "Certified executive and leadership coaching, applied to consequential decisions.",
                    },
                    {
                      n: "04",
                      label: "Facilitation",
                      body: "People-centered facilitation when conduct, culture, or trust requires careful handling.",
                    },
                    {
                      n: "05",
                      label: "Speaking",
                      body: "Authority content delivered into boards, leadership teams, and broader audiences.",
                    },
                  ].map((d) => (
                    <div
                      key={d.label}
                      className="p-6 lg:p-7 flex flex-col"
                      style={{ background: C.bg }}
                    >
                      <div className="flex items-baseline gap-3 mb-3">
                        <span
                          className="font-sora text-[11px] uppercase tracking-[0.22em] font-semibold"
                          style={{ color: C.brand }}
                        >
                          {d.n}
                        </span>
                        <h4
                          className="font-sora font-semibold text-[17px] leading-tight tracking-[-0.01em]"
                          style={{ color: C.text }}
                        >
                          {d.label}
                        </h4>
                      </div>
                      <p
                        className="text-[13.5px] leading-[1.65]"
                        style={{ color: `${C.text}BF` }}
                      >
                        {d.body}
                      </p>
                    </div>
                  ))}
                  {/* Trailing tile — explanatory hook */}
                  <div
                    className="p-6 lg:p-7 flex flex-col justify-between"
                    style={{ background: C.inverse, color: C.linen }}
                  >
                    <div>
                      <span
                        className="font-sora text-[11px] uppercase tracking-[0.22em] font-semibold"
                        style={{ color: C.rose }}
                      >
                        One practice
                      </span>
                      <p
                        className="mt-3 font-sora text-[17px] leading-[1.45] font-medium"
                        style={{ color: C.linen }}
                      >
                        Five disciplines, held together by judgment that doesn’t
                        treat them as separate.
                      </p>
                    </div>
                    <a
                      href="#cta"
                      className="mt-6 inline-flex items-center gap-2 font-sora text-[11px] uppercase tracking-[0.22em] font-semibold pb-1.5 border-b w-fit transition-all"
                      style={{ color: C.linen, borderColor: `${C.linen}66` }}
                    >
                      Discuss your moment <ArrowRight size={13} weight="bold" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insights / teaching */}
      <section
        data-testid="insights-section"
        className="border-b"
        style={{ background: C.surface, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="flex items-end justify-between mb-14 gap-6 flex-wrap">
            <div>
              <p
                className="font-sora text-[10.5px] uppercase tracking-[0.24em] mb-5 font-semibold"
                style={{ color: C.brand }}
              >
                Teaching · Current thinking · 05
              </p>
              <h2
                className="font-sora font-bold text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-[-0.02em]"
                style={{ color: C.text }}
              >
                How we think about the work.
              </h2>
            </div>
            <a
              href="#"
              className="group inline-flex items-center gap-2 font-sora text-[12px] uppercase tracking-[0.2em] font-semibold pb-1.5 border-b transition-all"
              style={{ color: C.brand, borderColor: C.brand }}
            >
              All insights
              <ArrowUpRight
                size={14}
                weight="bold"
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </a>
          </div>

          <div
            className="grid md:grid-cols-3 gap-px border"
            style={{ background: C.line, borderColor: C.line }}
          >
            {insights.map((post, i) => (
              <article
                key={i}
                data-testid={`insight-${i}`}
                className="p-8 lg:p-9 flex flex-col group transition-colors"
                style={{ background: C.linen }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = C.linen)}
              >
                <div className="flex items-center gap-2 mb-6">
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: C.brand }}
                  />
                  <p
                    className="font-sora text-[10.5px] uppercase tracking-[0.22em] font-semibold"
                    style={{ color: C.brand }}
                  >
                    {post.label}
                  </p>
                </div>
                <h3
                  className="font-sora font-bold text-[22px] lg:text-[23px] leading-[1.22] tracking-[-0.01em] mb-10 flex-1"
                  style={{ color: C.text }}
                >
                  {post.title}
                </h3>
                <div
                  className="flex items-center justify-between pt-5 border-t"
                  style={{ borderColor: `${C.text}1F` }}
                >
                  <span
                    className="font-sora text-[11px] uppercase tracking-[0.2em] font-medium"
                    style={{ color: `${C.text}A8` }}
                  >
                    {post.read}
                  </span>
                  <span
                    className="inline-flex items-center gap-1.5 font-sora text-[11px] uppercase tracking-[0.2em] font-semibold group-hover:gap-2.5 transition-all"
                    style={{ color: C.brand }}
                  >
                    Read
                    <ArrowRight size={13} weight="bold" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — anchored, dark, authoritative */}
      <section
        id="cta"
        data-testid="inquiry-cta"
        style={{ background: C.inverse, color: C.linen }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-28 lg:py-36">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <p
                className="font-sora text-[10.5px] uppercase tracking-[0.24em] mb-7 font-semibold"
                style={{ color: C.rose }}
              >
                Consultation · 06
              </p>
              <h2
                className="font-sora font-bold text-4xl sm:text-5xl lg:text-[78px] leading-[1.02] tracking-[-0.02em]"
                style={{ color: C.linen }}
              >
                Discuss your organization’s{" "}
                <span style={{ color: C.rose }}>moment</span>.
              </h2>
              <p
                className="mt-10 max-w-2xl text-[18px] lg:text-[19px] leading-[1.6]"
                style={{ color: `${C.linen}D5` }}
              >
                Share context for us to understand the work in front of you.
                We&rsquo;ll diagnose fit together and recommend a shape — even
                if that means pointing you elsewhere.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end space-y-4 w-full lg:w-auto">
              <a
                href="#"
                data-testid="cta-primary"
                className="group inline-flex items-center justify-between w-full lg:w-[300px] gap-6 px-7 py-5 font-sora text-[13px] uppercase tracking-[0.16em] font-semibold transition-colors"
                style={{ background: C.linen, color: C.inverse }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.rose)}
                onMouseLeave={(e) => (e.currentTarget.style.background = C.linen)}
              >
                Request a consultation
                <ArrowUpRight
                  size={16}
                  weight="bold"
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>
              <a
                href="#"
                data-testid="cta-secondary"
                className="group inline-flex items-center justify-between w-full lg:w-[300px] gap-6 border px-7 py-5 font-sora text-[13px] uppercase tracking-[0.16em] font-medium transition-colors"
                style={{ borderColor: `${C.linen}55`, color: C.linen }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.linen)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${C.linen}55`)}
              >
                Send a referral
                <ArrowRight
                  size={15}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="border-t"
        style={{ background: C.bg, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16 grid md:grid-cols-3 gap-12">
          <div>
            <BrandMark size="lg" />
            <p
              className="font-sora text-[11px] uppercase tracking-[0.24em] mt-4 font-semibold"
              style={{ color: C.brand }}
            >
              {TAGLINE}
            </p>
            <p
              className="text-[14.5px] leading-[1.7] max-w-xs mt-5"
              style={{ color: `${C.text}D0` }}
            >
              People practices that hold up as your mission grows.
            </p>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-3 gap-8">
            {[
              ["Pathways", ["Advisory", "Supervisory Readiness", "Coaching", "Facilitation", "Speaking"]],
              ["Process", ["Discover", "Assess", "Align", "Support"]],
              ["Connect", ["Consultation", "Insights", "Speaking"]],
            ].map(([title, items]) => (
              <div key={title}>
                <div
                  className="font-sora text-[10.5px] uppercase tracking-[0.26em] mb-5 pb-3 border-b font-semibold"
                  style={{ color: C.brand, borderColor: `${C.text}1F` }}
                >
                  {title}
                </div>
                <ul className="space-y-3">
                  {items.map((i) => (
                    <li key={i}>
                      <a
                        href="#"
                        className="text-[14px] transition-colors"
                        style={{ color: `${C.text}E0` }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = C.brand)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = `${C.text}E0`)}
                      >
                        {i}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t" style={{ borderColor: C.line }}>
          <div
            className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-5 flex flex-wrap items-center justify-between gap-3 font-sora text-[11px] uppercase tracking-[0.24em] font-medium"
            style={{ color: `${C.text}B0` }}
          >
            <span>© {new Date().getFullYear()} Faith 1st · Concept 02</span>
            <span style={{ color: C.brand }}>Systems That Hold</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
